import { DateTime } from 'luxon';

/**
 * Whether the home page's re-save of a day's unsaved entries runs now
 * (`UserDb.resaveCachedEntriesForDayAsync`).
 *
 * A module of its own, free of Angular and of the database, so that it can be unit tested — the
 * same reason `user-db.backlog-save.ts` sits beside the service.
 */

/** What the re-save does with one day. */
export enum ResaveDayDecision {
  /** nothing: the day's entries are saved some other way, or not at all */
  Skip = 'Skip',
  /** nothing yet: the day is re-saved once this session's sync has succeeded */
  Hold = 'Hold',
  /** save the day's unsaved entries now */
  Save = 'Save',
}

/**
 * Choose what the re-save does with `day`:
 *  - a day after today is skipped;
 *  - a past day is skipped until the sync has succeeded. Its unsaved entries were built from a
 *    schedule the sync has not confirmed yet, and the rebuild that follows the sync's success
 *    saves them instead (#6280);
 *  - today is held until the sync has succeeded (#6828). Another device may already have saved
 *    the same entries, and saving this device's empty copies first makes the two conflict. Once
 *    the sync has brought those copies in, the re-save runs again and skips them;
 *  - any other day is saved now.
 *
 * Only the untouched entries wait. An entry the user edits is saved by the ordinary save path,
 * which this does not touch.
 *
 * @param day the day being opened, start of day in the device's zone
 * @param today the device's today, start of day
 * @param syncSucceeded whether this session's first sync of the user database has succeeded
 */
export function chooseResaveForDay(day: DateTime, today: DateTime, syncSucceeded: boolean): ResaveDayDecision {
  const dayIso = day.toISODate();
  const todayIso = today.toISODate();
  // if the day is in the future, nothing is saved
  if (dayIso > todayIso) {
    return ResaveDayDecision.Skip;
  }
  // if a past day is opened before the sync succeeded, the rebuild after the sync saves it
  if (dayIso < todayIso && !syncSucceeded) {
    return ResaveDayDecision.Skip;
  }
  // if today is opened before the sync succeeded, it waits for the sync (#6828)
  if (!syncSucceeded) {
    return ResaveDayDecision.Hold;
  }
  return ResaveDayDecision.Save;
}
